/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { inject, Injectable } from '@angular/core';
import { getEmbeddedResource, Resource, ResourceUri } from '@ngxp/rest';
import { isEmpty, isNull } from 'lodash-es';
import { BehaviorSubject, filter, map, Observable, withLatestFrom } from 'rxjs';
import { ListResourceStateService, StateService } from '../ngrx/state.service';
import { ResourceLoader } from './resource.loader';
import { ListItemResource, ListResourceServiceConfig } from './resource.model';
import { mapToResource } from './resource.rxjs.operator';
import {
  cloneDeepListResourceSorted,
  createEmptyStateResource,
  createStateResource,
  getEmbeddedResources,
  getEmbeddedResourcesAsArray,
  ListResource,
  StateResource,
} from './resource.util';

@Injectable()
export abstract class ListResourceService<B extends Resource, T extends ListResource, I extends ListItemResource> {
  private readonly stateService: StateService = inject(StateService);

  protected resourceStateService: ListResourceStateService<T>;
  _resourceLoader: ResourceLoader<B, T>;

  readonly selectedResourceUri: BehaviorSubject<ResourceUri> = new BehaviorSubject(null);

  protected constructor(protected config: ListResourceServiceConfig<B>) {
    this.initStateService();
    this.initResourceLoader();
  }

  private initStateService(): void {
    this.resourceStateService = this.stateService.createListResourceStateService(this.config.stateInfo);
  }

  private initResourceLoader(): void {
    this._resourceLoader = new ResourceLoader<B, T>(this.config, this.resourceStateService);
  }

  public getList(compareFn: (toCompare: I, compareWith: I) => number = () => 0): Observable<StateResource<T>> {
    return this._resourceLoader
      .get()
      .pipe(map((stateResource: StateResource<T>) => this.sortStateListResource(stateResource, compareFn)));
  }

  private sortStateListResource(
    stateResource: StateResource<T>,
    compareFn: (toCompare: I, compareWith: I) => number,
  ): StateResource<T> {
    return {
      ...stateResource,
      resource: cloneDeepListResourceSorted(stateResource.resource, this.config.listResourceListLinkRel, compareFn),
    };
  }

  public select(uri: ResourceUri): void {
    this.selectedResourceUri.next(uri);
  }

  public unselect(): void {
    this.selectedResourceUri.next(null);
  }

  public getSelected(): Observable<StateResource<I>> {
    return this.selectedResourceUri.asObservable().pipe(
      withLatestFrom(this.selectItems()),
      map(([uri, items]) => this.toSelectedState(uri, items)),
    );
  }
  private toSelectedState(uri: ResourceUri, items: I[]): StateResource<I> {
    if (!uri) return createEmptyStateResource();

    const found: I = items.find((item) => item._links.self.href === uri);
    return createStateResource(found);
  }

  public refresh(): void {
    this.resourceStateService.reloadResource();
  }

  public getItems(): Observable<I[]> {
    return this.getList().pipe(
      filter((listStateResource: StateResource<T>) => !listStateResource.loading),
      map((listStateResource: StateResource<T>) =>
        getEmbeddedResources<I>(listStateResource, this.config.listResourceListLinkRel),
      ),
    );
  }

  public selectItems(): Observable<I[]> {
    return this.selectListResource().pipe(
      map((resource: T) => getEmbeddedResourcesAsArray<I>(resource, this.config.listResourceListLinkRel)),
    );
  }

  public isListEmpty(): Observable<boolean> {
    return this.selectListResource().pipe(
      map(
        (listResource: T) =>
          isNull(listResource) || isEmpty(getEmbeddedResource(listResource, this.config.listResourceListLinkRel)),
      ),
    );
  }

  public selectListResource(): Observable<T> {
    return this.resourceStateService.selectResource().pipe(mapToResource<T>());
  }

  public clearList(): void {
    this.resourceStateService.clearResource();
  }

  public hasItem(matchFn: (item: I) => boolean): Observable<boolean> {
    return this.selectListResource().pipe(map((listResource: T) => this.containsItem(listResource, matchFn)));
  }

  private containsItem(listResource: T, matchFn: (item: I) => boolean): boolean {
    if (isNull(listResource)) return false;
    const embeddedResources: I[] = getEmbeddedResource(listResource, this.config.listResourceListLinkRel);
    return isNull(embeddedResources) ? false : embeddedResources.some((item: I) => matchFn(item));
  }
}
